import {MetricSeries, MetricSeriesAnalysis} from "@/lib/types/metricsExplorer.ts";
import {ExplorerChartType} from "@/pages/metricsExplorer/explorerConfig.ts";

/** Chart families that read the data differently, not just draw it differently. */
export const isTimeSeriesChart = (chartType: ExplorerChartType): boolean =>
  !["scatter", "distribution", "heatmap"].includes(chartType);

export interface ScatterPoint {
  x: number;
  y: number;
  time: string;
}

/**
 * Pair two series on their shared time buckets.
 *
 * Only buckets where both sides have a real value produce a point: a gap on either axis
 * says nothing about correlation, and treating it as zero would invent one.
 */
export const buildScatterPoints = (xSeries: MetricSeries, ySeries: MetricSeries): ScatterPoint[] => {
  const yByTime = new Map<string, number>();
  ySeries.datapoints.forEach((point) => {
    if (point.value !== null) {
      yByTime.set(point.time, point.value);
    }
  });
  const points: ScatterPoint[] = [];
  xSeries.datapoints.forEach((point) => {
    const paired = point.value !== null ? yByTime.get(point.time) : undefined;
    if (paired !== undefined && point.value !== null) {
      points.push({x: point.value, y: paired, time: point.time});
    }
  });
  return points;
};

/**
 * The two series a scatter plots against each other.
 *
 * A scatter needs exactly two things to compare. Rather than refuse when a chart has more,
 * take the first two queried series — the builder rail's order is the user's own.
 */
export const pickScatterPair = (series: MetricSeries[]): [MetricSeries, MetricSeries] | null => {
  const queried = series.filter((item) => item.kind === "PRIMARY");
  return queried.length >= 2 ? [queried[0], queried[1]] : null;
};

export interface HeatmapPoint {
  x: number;
  y: number;
  value: number | null;
}

export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Flatten the server's weekday-by-hour grid into Highcharts heatmap points.
 *
 * Empty slots are kept with a null value rather than dropped, so the grid stays a full
 * 7×24 and a quiet Sunday reads as absent data instead of collapsing the layout.
 */
export const buildHeatmapPoints = (grid: (number | null)[][]): HeatmapPoint[] => {
  const points: HeatmapPoint[] = [];
  grid.forEach((row, weekday) => {
    row.forEach((value, hour) => {
      points.push({x: hour, y: weekday, value});
    });
  });
  return points;
};

/** The analysis whose seasonality the heatmap draws: the first that actually has one. */
export const pickSeasonality = (analyses: MetricSeriesAnalysis[]): MetricSeriesAnalysis | undefined =>
  analyses.find((analysis) => analysis.seasonality !== null);

/** Values a distribution chart bins, with gaps removed. */
export const collectValues = (series: MetricSeries): number[] =>
  series.datapoints.map((point) => point.value).filter((value): value is number => value !== null);

export interface DowntimeWindow {
  from: number;
  to: number;
}

export interface CheckStateEvent {
  start: string;
  end: string | null;
  status: "UP" | "DOWN" | "NO_DATA";
}

/**
 * Turn the check's state events into plotband ranges.
 *
 * Only DOWN counts: NO_DATA means the check did not report, which is a gap in the line
 * rather than an outage, and shading it would overstate what is known.
 *
 * An unfinished outage has no end, so it is drawn to the edge of the window.
 */
export const buildDowntimeWindows = (events: CheckStateEvent[], rangeEnd: Date): DowntimeWindow[] =>
  events
    .filter((event) => event.status === "DOWN")
    .map((event) => ({
      from: Date.parse(event.start),
      to: event.end ? Date.parse(event.end) : rangeEnd.getTime(),
    }))
    .filter((window) => Number.isFinite(window.from) && Number.isFinite(window.to));
